import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type {
  Application,
  ApplicationPlatform,
  ApplicationStatus,
  VersionEntry,
  VersionEntryType,
} from '@/types'
import type { ApplicationsState } from './state'

export interface CreateApplicationData {
  projectId: string
  name: string
  description?: string
  platform: ApplicationPlatform
  currentVersion?: string
  technologies?: string[]
  repositoryUrl?: string
  productionUrl?: string
  stagingUrl?: string
}

export interface UpdateApplicationData {
  name?: string
  description?: string
  platform?: ApplicationPlatform
  status?: ApplicationStatus
  currentVersion?: string
  technologies?: string[]
  repositoryUrl?: string
  productionUrl?: string
  stagingUrl?: string
  contextDocumentId?: string
  agentDocumentId?: string
  planningDocumentId?: string
  pinnedDocuments?: import('@/types').PinnedDocument[]
}

export interface AddVersionEntryData {
  version: string
  type: VersionEntryType
  releaseDate: Date
  notes?: string
}

// Remove undefined values from an object (Firestore rejects undefined)
const stripUndefined = (obj: Record<string, unknown>): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  )
}

export const createActions = (state: ApplicationsState) => {
  const { logActivity } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const mapAppDoc = (d: { id: string; data: () => Record<string, unknown> }): Application => {
    const data = d.data()
    return {
      id: d.id,
      tenantId: data.tenantId,
      ...data,
      operatorIds: data.operatorIds || [],
      versionHistory: ((data.versionHistory || []) as Record<string, unknown>[]).map((v) => ({
        ...v,
        releaseDate: (v.releaseDate as any)?.toDate?.() || new Date(),
      })),
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
    } as Application
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(collection(db, 'applications'), ...baseConstraints, orderBy('createdAt', 'desc'))

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        q = query(collection(db, 'applications'), ...baseConstraints, where('organizationId', '==', authStore.user.organizationId), orderBy('createdAt', 'desc'))
      }
    } else if (authStore.user?.role === 'operator') {
      q = query(collection(db, 'applications'), ...baseConstraints, where('operatorIds', 'array-contains', authStore.user.id), orderBy('createdAt', 'desc'))
    }

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.applications = snapshot.docs.map(mapAppDoc)
      state.isLoading = false
    }, (error) => {
      console.error('Applications subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchApplications = async (projectId?: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      let q = query(collection(db, 'applications'), ...baseConstraints, orderBy('createdAt', 'desc'))

      if (projectId) {
        q = query(
          collection(db, 'applications'),
          ...baseConstraints,
          where('projectId', '==', projectId),
          orderBy('createdAt', 'desc')
        )
      } else if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'applications'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      } else if (authStore.user?.role === 'operator') {
        q = query(
          collection(db, 'applications'),
          ...baseConstraints,
          where('operatorIds', 'array-contains', authStore.user.id),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      state.applications = snapshot.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          tenantId: data.tenantId,
          ...data,
          operatorIds: data.operatorIds || [],
          versionHistory: (data.versionHistory || []).map((v: Record<string, unknown>) => ({
            ...v,
            releaseDate: (v.releaseDate as { toDate?: () => Date })?.toDate?.() || new Date(),
          })),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Application
      })
    } catch (error) {
      console.error('Error fetching applications:', error)
      state.error = 'Error al cargar las aplicaciones'
    } finally {
      state.isLoading = false
    }
  }

  const fetchApplicationById = async (id: string): Promise<Application | null> => {
    try {
      const docRef = doc(db, 'applications', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        const application = {
          id: docSnap.id,
          tenantId: data.tenantId,
          ...data,
          operatorIds: data.operatorIds || [],
          versionHistory: (data.versionHistory || []).map((v: Record<string, unknown>) => ({
            ...v,
            releaseDate: (v.releaseDate as { toDate?: () => Date })?.toDate?.() || new Date(),
          })),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Application

        state.selectedApplication = application
        return application
      }

      return null
    } catch (error) {
      console.error('Error fetching application:', error)
      state.error = 'Error al cargar la aplicacion'
      return null
    }
  }

  const createApplication = async (data: CreateApplicationData): Promise<string> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      // Get project to get organizationId
      const projectRef = doc(db, 'projects', data.projectId)
      const projectSnap = await getDoc(projectRef)

      if (!projectSnap.exists()) {
        throw new Error('Proyecto no encontrado')
      }

      const projectData = projectSnap.data()

      const applicationData = stripUndefined({
        ...getTenantData(),
        ...data,
        organizationId: projectData.organizationId,
        operatorIds: projectData.operatorIds || [],
        status: 'planning' as ApplicationStatus,
        technologies: data.technologies || [],
        versionHistory: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      })

      const docRef = await addDoc(collection(db, 'applications'), applicationData)

      await logActivity(
        'application',
        docRef.id,
        'created',
        `Se creo la aplicacion "${data.name}"`
      )

      await notificationsStore.notifyAdminsAndOperators(
        'project_created',
        'Nueva aplicacion creada',
        `Se ha creado la aplicacion "${data.name}"`,
        'application',
        docRef.id
      )

      await fetchApplications(data.projectId)

      return docRef.id
    } catch (error) {
      console.error('Error creating application:', error)
      state.error = 'Error al crear la aplicacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateApplication = async (id: string, data: UpdateApplicationData): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const updateData = stripUndefined({
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      await updateDoc(doc(db, 'applications', id), updateData)

      await logActivity(
        'application',
        id,
        data.status ? 'status_changed' : 'updated',
        data.status
          ? `Se cambio el estado de la aplicacion a "${data.status}"`
          : 'Se actualizo la aplicacion'
      )

      // Update local state
      const index = state.applications.findIndex((a) => a.id === id)
      if (index !== -1) {
        state.applications[index] = {
          ...state.applications[index],
          ...data,
          updatedAt: new Date(),
        }
      }

      if (state.selectedApplication?.id === id) {
        state.selectedApplication = {
          ...state.selectedApplication,
          ...data,
          updatedAt: new Date(),
        }
      }
    } catch (error) {
      console.error('Error updating application:', error)
      state.error = 'Error al actualizar la aplicacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteApplication = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const application = state.applications.find((a) => a.id === id)

      await deleteDoc(doc(db, 'applications', id))

      await logActivity(
        'application',
        id,
        'deleted',
        `Se elimino la aplicacion "${application?.name || id}"`
      )

      state.applications = state.applications.filter((a) => a.id !== id)

      if (state.selectedApplication?.id === id) {
        state.selectedApplication = null
      }
    } catch (error) {
      console.error('Error deleting application:', error)
      state.error = 'Error al eliminar la aplicacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const addVersionEntry = async (
    applicationId: string,
    data: AddVersionEntryData
  ): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const localEntry: VersionEntry = {
        id: crypto.randomUUID(),
        version: data.version,
        type: data.type,
        releaseDate: data.releaseDate,
        notes: data.notes,
        createdBy: userId,
      }

      // Strip undefined for Firestore
      const firestoreEntry = stripUndefined({ ...localEntry })

      await updateDoc(doc(db, 'applications', applicationId), {
        versionHistory: arrayUnion(firestoreEntry),
        currentVersion: data.version,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      await logActivity(
        'application',
        applicationId,
        'updated',
        `Se agrego la version "${data.version}"`
      )

      // Update local state
      const index = state.applications.findIndex((a) => a.id === applicationId)
      if (index !== -1) {
        state.applications[index] = {
          ...state.applications[index],
          currentVersion: data.version,
          versionHistory: [...state.applications[index].versionHistory, localEntry],
          updatedAt: new Date(),
        }
      }

      if (state.selectedApplication?.id === applicationId) {
        state.selectedApplication = {
          ...state.selectedApplication,
          currentVersion: data.version,
          versionHistory: [...state.selectedApplication.versionHistory, localEntry],
          updatedAt: new Date(),
        }
      }
    } catch (error) {
      console.error('Error adding version entry:', error)
      state.error = 'Error al agregar la version'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const setFilters = (filters: Partial<ApplicationsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearFilters = (): void => {
    state.filters = {
      search: '',
      projectId: '',
      status: '',
      platform: '',
    }
  }

  const setSelectedApplication = (application: Application | null): void => {
    state.selectedApplication = application
  }

  const assignOperatorToApplication = async (applicationId: string, operatorId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.user?.id || 'system'

      await updateDoc(doc(db, 'applications', applicationId), {
        operatorIds: arrayUnion(operatorId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      // Update local state
      const app = state.applications.find((a) => a.id === applicationId)
      if (app) {
        if (!app.operatorIds) app.operatorIds = []
        if (!app.operatorIds.includes(operatorId)) {
          app.operatorIds = [...app.operatorIds, operatorId]
        }
      }
      if (state.selectedApplication?.id === applicationId) {
        const ops = state.selectedApplication.operatorIds || []
        if (!ops.includes(operatorId)) {
          state.selectedApplication = {
            ...state.selectedApplication,
            operatorIds: [...ops, operatorId],
          }
        }
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al asignar operador'
      throw error
    }
  }

  const unassignOperatorFromApplication = async (applicationId: string, operatorId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.user?.id || 'system'

      await updateDoc(doc(db, 'applications', applicationId), {
        operatorIds: arrayRemove(operatorId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      // Update local state
      const app = state.applications.find((a) => a.id === applicationId)
      if (app && app.operatorIds) {
        app.operatorIds = app.operatorIds.filter((id) => id !== operatorId)
      }
      if (state.selectedApplication?.id === applicationId && state.selectedApplication.operatorIds) {
        state.selectedApplication = {
          ...state.selectedApplication,
          operatorIds: state.selectedApplication.operatorIds.filter((id) => id !== operatorId),
        }
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al desasignar operador'
      throw error
    }
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchApplications,
    fetchApplicationById,
    createApplication,
    updateApplication,
    deleteApplication,
    addVersionEntry,
    assignOperatorToApplication,
    unassignOperatorFromApplication,
    setFilters,
    clearFilters,
    setSelectedApplication,
  }
}
